<template>
  <PageView :page-reference="PAGE_REFERENCES.PORTFOLIO" :current-object-type="default_object_type_obj">
    <template #header>
      <TripleMenu
        :set-edit-mode="setEditMode"
        :edit-mode-active="edit_mode_active"
        :quick-actions="top_right_menu_quick_actions"
        :components-actions="top_right_menu_components_actions"
        :current-template="current_template"
        :page-reference="PAGE_REFERENCES.PORTFOLIO" />
    </template>
    <template #body>
      <LayoutContainer
        v-model:current-template="current_template"
        :set-edit-mode="setEditMode"
        :edit-mode-active="edit_mode_active"
        class="mt-5"
        :page-reference="PAGE_REFERENCES.PORTFOLIO" />
      <EmptyPortfolioPage v-if="!current_template" class="mt-[20vh]" />
    </template>
  </PageView>
  <Modal v-model:is-open="show_confirm_unsave_layout">
    <div class="sm:flex sm:items-start">
      <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
        <CircleHelp class="w-8 h-8 text-red-600" aria-hidden="true" />
      </div>
      <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
        <div as="h3" class="text-lg font-medium leading-6 text-gray-900">
          {{ $t("Unsaved Layout Changes") }}
        </div>
        <div class="flex flex-col mt-2">
          <p class="font-semibold text-md">
            {{ $t("Do you really want to exit this page? Any unsaved changes will be disregarded.") }}
          </p>
        </div>
      </div>
    </div>
    <div class="gap-2 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
      <button
        type="button"
        class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark sm:mt-0 sm:w-auto sm:text-sm"
        @click="handleModalResponse('no')">
        {{ $t("Cancel") }}
      </button>
      <button
        type="button"
        class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
        @click="handleModalResponse('yes')">
        {{ $t("Yes") }}
      </button>
    </div>
  </Modal>
  <Modal v-model:is-open="show_onboarding_modal" class="w-full sm:w-4/5 mx-auto">
    <div class="flex items-start flex-col">
      <div class="flex w-full">
        <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-green-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
          <Sparkles class="w-8 h-8 text-green-600" aria-hidden="true" />
        </div>
        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
          <h3 class="text-lg font-bold leading-6 text-gray-900">
            {{ $t("Welcome to RevSetter") }} 🎉
          </h3>
          <p class="mt-1 text-sm text-gray-600">
            {{ $t("We're excited to have you onboard!") }}
          </p>
        </div>
      </div>
      <!-- Video Section -->
      <div class="mt-4 w-full">
        <div class="relative w-full" style="padding-bottom: 56.25%; height: 0">
          <iframe
            credentialless="true"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            src="https://www.guidejar.com/embed/c8cc531f-60d4-43cd-a6ad-e3a7e4431722?type=1&controls=on"
            width="100%"
            height="100%"
            style="position: absolute; top: 0; left: 0"
            allowfullscreen
            frameborder="0" />
        </div>
      </div>
      <!-- Onboarding Steps -->
      <div class="mt-4 space-y-2 text-sm text-gray-700">
        <p>{{ $t("Here are 3 quick steps to get started") }}:</p>
        <ol class="list-decimal list-inside space-y-1">
          <li>{{ $t("Watch the onboarding video above") }}.</li>
          <li>
            {{ $t("Go to your") }}
            <router-link to="/homepage/rs-onboarding">
              <strong>{{ $t("Homepage") }}</strong>
            </router-link>
            {{ $t("to complete your RevSetter Onboarding.") }}
          </li>
          <li>{{ $t("Add customers and elevate your work!") }}</li>
        </ol>
        <p class="mt-2">
          {{ $t("Enjoy and don't hesitate to") }}
          <a href="mailto:customersuccess@revsetter.com" class="text-blue-600 underline">{{ $t("share feedback") }}</a>
          {{ $t("or reach out directly.") }}
        </p>
      </div>
    </div>
    <div class="flex items-center justify-between gap-4 mt-5 sm:flex sm:flex-row-reverse">
      <button
        type="button"
        class="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded hover:bg-blue-500 focus:outline-none"
        @click.prevent="dismissOnBoardingModal">
        {{ $t("Close") }}
      </button>
      <div class="flex items-center" @click.prevent="() => (dismiss_forever = !dismiss_forever)">
        <input
          id="dismiss_forever"
          v-model="dismiss_forever"
          type="checkbox"
          class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500">
        <label for="dismiss_forever" class="cursor-pointer ml-2 text-sm text-gray-600"> {{ $t("Don't show again") }} </label>
      </div>
    </div>
  </Modal>
</template>

<script>
import TripleMenu from "@/components/shared/TripleMenu.vue"
import EmptyPortfolioPage from "./EmptyPortfolioPage.vue"
import PageView from "@/components/shared/pages/PageView.vue"
import { PAGE_REFERENCES, VIEW_TYPES } from "@/store/constants/references"
import LayoutContainer from "@/components/shared/pages/LayoutContainer.vue"
import {
  ClipboardList,
  Mail,
  Folder,
  TrendingUp,
  MessagesSquare,
  List,
  CircleHelp,
  Sparkles,
  ChartNoAxesColumnDecreasing
} from "lucide-vue-next"
import ModuleIcon from "@/components/shared/icons/ModuleIcon.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import Modal from "@/components/shared/Modal.vue"

export default {
  components: {
    Modal,
    Sparkles,
    CircleHelp,
    PageView,
    LayoutContainer,
    TripleMenu,
    EmptyPortfolioPage,
  },
  inject: ["isModuleActivated", "canUserEditLayout"],
  async beforeRouteLeave(to, from, next) {
    if (this?.edit_mode_active) {
      const ignore_changes = await this.ignoreLayoutChanges()
      this.edit_mode_active = ignore_changes != "yes"
      if (ignore_changes == "yes") {
        next()
      } else {
        next(false)
      }
    } else {
      next(true)
    }
  },
  data() {
    return {
      PAGE_REFERENCES,
      dismiss_forever: false,
      show_onboarding_modal: false,
      current_template: null,
      default_object_type_obj: { type: "default" },
      edit_mode_active: false,
      show_confirm_unsave_layout: false,
      ignore_changes: false,
      mouse_event: null,
      testAgentId: "63ace2d8-77b9-4515-b5a4-f1e3fa47f4ba"
    }
  },
  computed: {
    can_user_edit_layout() {
      return this.canUserEditLayout(this.$store.state.auth.user, this.current_template)
    },
    top_right_menu_quick_actions() {
      return [
        {
          name: this.$t("Create a Group"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_NEW_GROUP, this.$utils.isUUID(this.$route.params.page) ? null : this.$route.params.page)
          },
          disabled: false,
          icon: Folder
        },
        {
          name: this.$t("Create a Plan"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_PLAN)
          },
          disabled: false,
          icon: List
        },
        {
          name: this.$t("Create a Goal"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_GOAL)
          },
          disabled: false,
          icon: TrendingUp
        },
        {
          name: this.$t("Create a Task"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_TASK)
          },
          disabled: false,
          icon: ClipboardList
        },
        {
          name: this.$t("Create an Interaction"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_INTERACTION)
          },
          disabled: false,
          icon: MessagesSquare
        },
        {
          name: this.$t("Send Email"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL)
          },
          disabled: false,
          icon: Mail
        },

        {
          name: this.$t("Create a Custom Metric"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_CUSTOM_METRIC, {
              object_type: this.$utils.isUUID(this.$route.params.page) ? null : this.$route.params.page,
              view_type: "table"
            })
          },
          disabled: false,
          icon: ChartNoAxesColumnDecreasing
        }
      ]
    },
    top_right_menu_components_actions() {
      return [
        {
          name: this.can_user_edit_layout ? this.$t("Edit Layout") : this.$t("Edit Layout (View Only)"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.EDIT_LAYOUT, {
              page_reference: PAGE_REFERENCES.PORTFOLIO,
              view_type: VIEW_TYPES.ROLLUPS,
              page_name: this.$route.params.page
            })
          },
          disabled: !this.can_user_edit_layout,
          icon: ModuleIcon,
          keyBoardShortcut: this.can_user_edit_layout ? "I" : null
        },
        {
          name: this.can_user_edit_layout ? this.$t("Add/Remove Components") : this.$t("Add/Remove Components (View Only)"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.ADD_NEW_COMPONENT, {
              page_reference: PAGE_REFERENCES.PORTFOLIO,
              current_template: this.current_template
            })
          },
          disabled: !this.can_user_edit_layout,
          keyBoardShortcut: this.can_user_edit_layout ? "U" : null,
          icon: ModuleIcon
        },

        {
          name: this.current_template?.is_static ? this.$t("Reset Page") : this.$t("Delete Page"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.DELETE_PAGE, this.current_template)
          },
          icon: ModuleIcon
        }
      ]
    },
    should_show_onboarding_modal() {
      return (
        !this.$store.state.ui_options.portfolio_dismiss_onboarding_video_forever &&
        !(localStorage.getItem("is_next_login") && localStorage.getItem("portfolio_dismiss_onboarding_video_till_next_login"))
      )
    }
  },
  unmounted() {
    if (this.mouse_event) {
      window.removeEventListener("mousemove", this.mouse_event)
    }
  },
  mounted() {
    if (this.$route.query.cpc == "true") {
      this.$store.dispatch("ui/setUIValue", {
        portfolio_dismiss_onboarding_video_forever: false
      })
    }
    this.show_confirm_unsave_layout = false

    if (this.should_show_onboarding_modal) {
      setTimeout(() => {
        this.show_onboarding_modal = this.should_show_onboarding_modal
      }, 2000)
    }
  },
  methods: {
    dismissOnBoardingModal() {
      this.show_onboarding_modal = false
      if (this.dismiss_forever) {
        this.$store.dispatch("ui/setUIValue", {
          portfolio_dismiss_onboarding_video_forever: true
        })
      } else {
        localStorage.setItem("is_next_login", false)
        localStorage.setItem("portfolio_dismiss_onboarding_video_till_next_login", true)
      }
    },
    ignoreLayoutChanges() {
      return new Promise((resolve) => {
        this.show_confirm_unsave_layout = true
        // We Use a watcher to detect when the user has made a decision
        const unwatch = this.$watch("ignore_changes", (newValue) => {
          if (newValue != undefined) {
            this.show_confirm_unsave_layout = false
            // give the modal time to close
            setTimeout(() => {
              resolve(newValue)
              // Remove the watcher once the decision has been made
              unwatch()
              // Reset the value to undefined super important
              this.ignore_changes = undefined
            }, 300)
          }
        })
      })
    },
    handleModalResponse(answer) {
      this.ignore_changes = answer
    },
    setEditMode(value) {
      this.edit_mode_active = value
    }
  }
}
</script>
